<template>
  <div class="blog-stage" :class="rootClass" :style="rootStyle">
    <div class="blog-stage__blurred-image" v-if="blogImage">
      <div class="blog-stage__blurred-gradient" />
    </div>
    <div class="blog-stage__header-container">
      <div class="blog-stage__header">
        <div class="blog-stage__upper-meta">
          <span>
            {{ formattedDate }}
          </span>
          <template v-if="blogCategories">
            |
            <ul class="blog-stage__categories">
              <li>
                <nuxt-link class="blog-stage__category-link" to="/blog/">Blog</nuxt-link>&nbsp;
              </li>
              <li v-for="category in blogCategories" :key="category.name" class="blog-stage__category">
                <nuxt-link class="blog-stage__category-link" :to="category.uri">
                  {{ category.name }}
                </nuxt-link>
              </li>
            </ul>
          </template>
        </div>
        <div class="blog-stage__actions">
          <preferred-source-button />
          <span class="blog-stage__actions-divider" aria-hidden="true" />
          <social-share class="blog-stage__socials" />
        </div>
        <h1 class="blog-stage__headline" v-html="blogHeadline" />
        <div v-if="blogAuthor" class="blog-stage__author-wrapper">
          <nuxt-link v-if="blogAuthor.uri" :to="blogAuthor.uri" class="blog-stage__author">
            By {{ blogAuthor.name }}
          </nuxt-link>
          <span v-else-if="blogAuthor" class="blog-stage__author">
          By {{ blogAuthor.name }}
        </span>
        </div>

        <u-i-image-handler
          v-if="blogImage"
          format="avif"
          densities="1x 2x"
          sizes="100% xl:980px"
          class="blog-stage__header-image"
          :src="blogImage.mediaItemUrl"
          :alt="blogImage.altText" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useImage } from "#image/composables";
import SocialShare from "~/components/blocks/SocialShare.vue";
import PreferredSourceButton from "~/components/blocks/PreferredSourceButton.vue";

export default {
  components: {SocialShare, PreferredSourceButton},
  setup () {
    const img = useImage();

    return {
      img,
    };
  },
  props: {
    blogDate: String,
    blogCategories: Array as Array<{ name: string, uri: string }>,
    blogImage: Object as { mediaItemUrl: string, altText: string },
    blogHeadline: String,
    blogAuthor: Object as { name: string, uri: string, }|null,
    blogExcerpt: String,
  },
  computed: {
    rootClass () {
      return {
        'blog-stage--no-image': !this.blogImage,
        'blog-stage--with-image': !!this.blogImage,
      }
    },
    rootStyle () {
      if (!this.blogImage) {
        return {};
      }

      const imageUrl = this.img(this.blogImage.mediaItemUrl, {
        blur: 40,
        height: 225,
        width: 400,
      });

      return {
        '--blurred-image': `url(${imageUrl})`,
      };
    },
    formattedDate () {
      if (this.blogDate) {
        const date = new Date(this.blogDate);

        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      }

      return '';
    }
  },
};
</script>

<style lang="scss">
@use "~/common/scss/helpers";
@use "~/common/scss/variables";
@use "~/common/scss/typography";
@use "~/common/scss/mq";

.blog-stage {
  $root: &;

  color: #FFFFFF;
  display: grid;
  grid-template: "header" auto "excerpt" auto / 100%;
  width: 100%;

  &--no-image {
    color: #000;
  }

  &__blurred-image {
    background-image: var(--blurred-image);
    background-size: cover;
    background-position: 50% 50%;
    grid-area: header;
  }

  &__blurred-gradient {
    background: linear-gradient(to bottom, #00000033, #00000033 75%, #FFFFFFFF 95%, #FFFFFF);
    height: 100%;
    width: 100%;
  }

  &__header-container {
    grid-area: header;

    #{$root}--with-image & {
      backdrop-filter: blur(10px);
    }
  }

  &__header {
    display: grid;
    grid-template: "socials" auto "upperMeta" auto "headline" auto "author" auto "image" auto / 100%;
    margin: 0 auto;
    max-width: variables.$containerMediumSmall;
    padding-top: helpers.px-to-rem(30);
    width: 95%;
    z-index: 10;

    @include mq.mq(variables.$mq-2-min) {
      padding-top: helpers.px-to-rem(60);
      grid-template: "upperMeta socials" auto "headline headline" auto "author author" auto "image image" auto / 1fr auto;
    }

    #{$root}--with-image & {
      min-height: 60vh;
    }
  }

  &__upper-meta {
    align-items: center;
    display: flex;
    gap: .5rem;
    grid-area: upperMeta;
    font-weight: 200;
  }

  &__categories {
    align-items: center;
    display: flex;
    flex-flow: row wrap;
    list-style: none;
    padding: 0 0;
    font-weight: 200;
  }

  &__category {
    margin-right: .25rem;
    font-weight: 200;

    &:not(:first-child) {

      &::before {
        content: "•";
        margin-right: .25rem;
      }
    }
  }

  &__category-link {
    color: currentColor;
    padding: .25rem .25rem;
    text-transform: uppercase;
  }

  &__actions {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: helpers.px-to-rem(10);
    grid-area: socials;
    justify-content: flex-end;
    min-height: 44px;
  }

  &__actions-divider {
    background: currentColor;
    height: 18px;
    opacity: .45;
    width: 1px;
  }

  &__socials {
    --share-color: currentColor;
    --share-color-hover: #{variables.$colorGreyActive};
    --share-size: #{helpers.px-to-rem(26)};

    align-items: center;
    display: flex;
    height: var(--share-size);
    justify-content: flex-end;

    .social-share__icons {
      gap: helpers.px-to-rem(5);
    }

    .social-share__icon {
      border: none;
      display: flex;
    }
  }

  &__headline {
    @include typography.h1();

    color: currentColor;
    grid-area: headline;
    padding-top: helpers.px-to-rem(30);
  }

  &__author-wrapper {
    grid-area: author;
  }

  &__author {
    color: currentColor;
  }

  &__header-image {
    align-self: flex-end;
    grid-area: image;
    height: 100%;
    padding-top: helpers.px-to-rem(30);
    width: 100%;

    @include mq.mq(variables.$mq-2-min) {
      padding-top: helpers.px-to-rem(100);
    }

    img {
      border-radius: 8px 8px 0 0;
      height: 100%;
      object-fit: cover;
      width: 100%;
    }
  }
}
</style>
